/**
 * Resume scoring rubric v2 — the justifiable score (F3, RECRUITMENT_FLOW_ANALYSIS 8 Aug 2026).
 *
 * WHY. The v1 score was the model's holistic guess: it invented its own components per resume
 * (the PM test case rendered exactly two bars, one with a truncated label), no weights, no band
 * definitions, no arithmetic. "80 / STRONG MATCH" could not be justified to a client — and a
 * requirement asking 5 years against a resume evidencing ~4.3 was never computed, never shown,
 * while a 2010–2020 employment gap went entirely unsurfaced.
 *
 * THE CONTRACT. Every resume on a requisition is scored against the SAME five components with the
 * SAME weights. The model's job shrinks to what a model is for — reading evidence and scoring
 * each component against its anchor, quoting the resume verbatim — and everything a client could
 * dispute is computed HERE, in code, deterministically:
 *
 *   - relevant years   summed from the model-extracted dated roles it marked relevant, overlap-
 *                      merged, compared against the requirement's minimum in code;
 *   - employment gaps  detected from the same date ranges, in code;
 *   - the OVERALL      a weighted sum of component scores. The model never invents the headline
 *                      number, so the headline number can always be reproduced from the parts.
 *
 * Gaps and shortfalls are SURFACED, never auto-penalised into rejection — the engine ranks, a
 * human decides (GROUND_RULES). The experience component's score does reflect a shortfall, but
 * the report says exactly why in plain words.
 */
import { z } from "zod";
export const RESUME_RUBRIC_VERSION = 2;
export const RESUME_RUBRIC = [
    {
        id: "role_match",
        label: "Role match",
        weight: 30,
        measures: "Has this person actually done the JOB the requirement names — same kind of role, same kind of responsibility — not merely worked near it.",
    },
    {
        id: "experience_depth",
        label: "Experience depth",
        weight: 25,
        measures: "Years of directly relevant experience against what the requirement asks, and the seniority of what they owned at that time.",
    },
    {
        id: "skills_tools",
        label: "Skills & tools",
        weight: 20,
        measures: "The requirement's named skills, tools and methods, evidenced by use in a real role — not by a skills list alone.",
    },
    {
        id: "domain_relevance",
        label: "Domain relevance",
        weight: 15,
        measures: "How close their industries and problem domains are to the requirement's world.",
    },
    {
        id: "recency_continuity",
        label: "Recency & continuity",
        weight: 10,
        measures: "Whether the relevant experience is recent and the career timeline is continuous. Gaps are surfaced as facts for the interview, never scored as character.",
    },
];
export const RESUME_COMPONENT_IDS = RESUME_RUBRIC.map((c) => c.id);
export const RESUME_BANDS = [
    {
        min: 75,
        key: "strong",
        label: "Strong match",
        definition: "Meets the requirement's must-haves with direct, quoted evidence; any shortfalls are minor and listed.",
    },
    {
        min: 50,
        key: "moderate",
        label: "Moderate match",
        definition: "Meets most of the requirement; the specific gaps are listed and worth probing at interview.",
    },
    {
        min: 0,
        key: "weak",
        label: "Weak match",
        definition: "Material gaps against the requirement's must-haves; the evidence found is listed so the call is yours.",
    },
];
export function bandFor(score) {
    return RESUME_BANDS.find((b) => score >= b.min) ?? RESUME_BANDS[RESUME_BANDS.length - 1];
}
// ── What the model extracts (validated) ───────────────────────────────────────
/** "YYYY-MM" — month precision is what resumes actually state. */
const yearMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
export const datedRoleSchema = z.object({
    title: z.string().min(1).max(160),
    organisation: z.string().max(160).default(""),
    start: yearMonth,
    /** null = "Present". */
    end: yearMonth.nullable(),
    /** Whether this role is RELEVANT to the requirement — the model judges, code sums. */
    relevant: z.boolean(),
    /** The city the role was based in, when the resume states one (feeds "cities worked"). */
    location: z.string().max(80).optional(),
});
export const rubricComponentScoreSchema = z.object({
    id: z.enum(RESUME_COMPONENT_IDS),
    score: z.number().int().min(0).max(100),
    /** VERBATIM quote from the resume that this score stands on. Empty only when score < 30. */
    evidence: z.string().max(500),
    /** What the requirement asks for that this resume does not show, in this component. */
    gaps: z.array(z.string().max(200)).max(5),
});
// ── The requirement profile — the published basis of every score ──────────────
export const requirementProfileSchema = z.object({
    role: z.string().max(160).default(""),
    minYears: z.number().min(0).max(50).nullable().default(null),
    mustHaves: z.array(z.string().max(160)).max(12).default([]),
    niceToHaves: z.array(z.string().max(160)).max(12).default([]),
    domain: z.string().max(160).default(""),
});
// ── Deterministic arithmetic (code, never the model) ──────────────────────────
const toMonths = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    return (y ?? 0) * 12 + ((m ?? 1) - 1);
};
/** Merge [start,end) month intervals; overlapping roles must not double-count. */
function mergeIntervals(spans) {
    const sorted = [...spans].sort((a, b) => a[0] - b[0]);
    const out = [];
    for (const [s, e] of sorted) {
        const last = out[out.length - 1];
        if (last && s <= last[1])
            last[1] = Math.max(last[1], e);
        else
            out.push([s, e]);
    }
    return out;
}
/**
 * Total years across the roles marked relevant, overlap-merged, month precision.
 * `now` is injected for testability; an open role runs to it.
 */
export function relevantYears(roles, now = new Date()) {
    const nowM = now.getFullYear() * 12 + now.getMonth();
    const spans = roles
        .filter((r) => r.relevant)
        .map((r) => [toMonths(r.start), Math.min(r.end === null ? nowM : toMonths(r.end), nowM) + 1])
        .filter(([s, e]) => e > s);
    const months = mergeIntervals(spans).reduce((sum, [s, e]) => sum + (e - s), 0);
    return Math.round((months / 12) * 10) / 10;
}
/** Total years across ALL dated roles (relevant or not), overlap-merged, month precision — the person's whole
 *  working history as the resume dates it. An open role runs to `now`. */
export function totalYears(roles, now = new Date()) {
    return relevantYears(roles.map((r) => ({ ...r, relevant: true })), now);
}
const fromMonths = (m) => `${Math.floor(m / 12)}-${String((m % 12) + 1).padStart(2, "0")}`;
/**
 * Gaps between ALL dated roles (relevant or not — a gap is a timeline fact, not a relevance
 * judgement), at least `minMonths` long. The tail after the last role is NOT a gap — the person
 * may simply be employed at a company the model failed to date; accusing them of a current gap
 * needs more than absence of parsing.
 */
export function employmentGaps(roles, minMonths = 6, now = new Date()) {
    const nowM = now.getFullYear() * 12 + now.getMonth();
    const spans = mergeIntervals(roles
        .map((r) => [toMonths(r.start), Math.min(r.end === null ? nowM : toMonths(r.end), nowM) + 1])
        .filter(([s, e]) => e > s));
    const out = [];
    for (let i = 1; i < spans.length; i++) {
        const gap = spans[i][0] - spans[i - 1][1];
        if (gap >= minMonths) {
            out.push({ fromYm: fromMonths(spans[i - 1][1]), toYm: fromMonths(spans[i][0]), months: gap });
        }
    }
    return out;
}
/**
 * THE overall. A weighted mean of the fixed components, rounded — nothing else. Missing
 * components (a degraded model reply) simply drop out with their weight, so a partial reply
 * yields an honest partial-basis score rather than treating absence as zero competence.
 */
export function computeOverall(components) {
    let sum = 0;
    let wsum = 0;
    for (const def of RESUME_RUBRIC) {
        const got = components.find((c) => c.id === def.id);
        if (!got)
            continue;
        sum += got.score * def.weight;
        wsum += def.weight;
    }
    if (wsum === 0)
        return 0;
    return Math.round(sum / wsum);
}
/** Human sentences for the flags the report shows. Written here so every surface says it the same way. */
export function experienceShortfallFlag(minYears, evidencedYears) {
    if (minYears === null || evidencedYears >= minYears)
        return null;
    return `Requirement asks for ${minYears} years; the resume evidences ~${evidencedYears} years of relevant experience.`;
}
export function gapFlag(g) {
    const years = Math.round((g.months / 12) * 10) / 10;
    return `Employment gap ${g.fromYm} to ${g.toYm} (~${years} years) — surfaced for the interview, not scored.`;
}
//# sourceMappingURL=resumeRubric.js.map